import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription, PlanTier } from "@/hooks/use-subscription";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Loader2, ArrowLeft } from "lucide-react";

interface Plan {
  id: string;
  tier: PlanTier;
  name: string;
  description: string | null;
  price_cents: number;
  trial_days: number;
}

interface PlanSelectionStepProps {
  onNext: () => void;
  onBack: () => void;
  onPlanSelected: (tier: PlanTier) => void;
}

const PLAN_HIGHLIGHTS: Record<PlanTier, string[]> = {
  freemium: [
    "1 conta corrente",
    "Categorias e transações ilimitadas",
    "1 meta financeira",
    "Relatórios básicos",
    "Análise de gastos com IA",
  ],
  starter: [
    "2 contas correntes",
    "2 contas poupança",
    "2 cartões de crédito",
    "Transferências entre contas",
    "Planejamento orçamentário",
    "Importação de extratos",
    "Metas ilimitadas",
  ],
  colab: [
    "Contas ilimitadas",
    "Compartilhamento com até 3 pessoas",
    "Relatório DRE",
    "Todos os recursos do Starter",
    "Provisões financeiras",
  ],
};

export function PlanSelectionStep({ onNext, onBack, onPlanSelected }: PlanSelectionStepProps) {
  const { openCheckout } = useSubscription();
  const [loadingTier, setLoadingTier] = useState<PlanTier | null>(null);

  const { data: plans, isLoading: plansLoading } = useQuery<Plan[]>({
    queryKey: ["plans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plans" as any)
        .select("*")
        .eq("is_active", true)
        .order("price_cents", { ascending: true });

      if (error) throw error;
      return (data as unknown as Plan[]) ?? [];
    },
    staleTime: 10 * 60 * 1000,
  });

  const handleSelectPlan = async (tier: PlanTier) => {
    setLoadingTier(tier);
    try {
      onPlanSelected(tier);

      if (tier === "freemium") {
        onNext();
      } else {
        await openCheckout(tier);
      }
    } catch {
      setLoadingTier(null);
    }
  };

  const formatPrice = (cents: number) => {
    if (cents === 0) return "Grátis";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  };

  if (plansLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 py-4">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold tracking-tight">Escolha seu plano</h2>
        <p className="text-muted-foreground">
          Comece sem compromisso. Cancele quando quiser.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(plans ?? []).map((plan) => {
          const isRecommended = plan.tier === "starter";
          const isPaid = plan.price_cents > 0;
          const highlights = PLAN_HIGHLIGHTS[plan.tier] ?? [];
          const isCurrentLoading = loadingTier === plan.tier;

          return (
            <Card
              key={plan.id}
              className={`relative flex flex-col ${
                isRecommended
                  ? "border-primary shadow-lg ring-2 ring-primary/20"
                  : ""
              }`}
            >
              {isRecommended && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  Recomendado
                </Badge>
              )}

              <CardHeader className="text-center pb-2">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                <CardDescription className="text-sm">
                  {plan.description}
                </CardDescription>
                <div className="pt-2">
                  <span className="text-3xl font-bold">
                    {formatPrice(plan.price_cents)}
                  </span>
                  {isPaid && (
                    <span className="text-sm text-muted-foreground">/mês</span>
                  )}
                </div>
                {isPaid && plan.trial_days > 0 && (
                  <Badge variant="secondary" className="mt-2">
                    {plan.trial_days} dias grátis
                  </Badge>
                )}
              </CardHeader>

              <CardContent className="flex-1 flex flex-col">
                <ul className="space-y-2 flex-1 mb-4">
                  {highlights.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelectPlan(plan.tier)}
                  variant={isRecommended ? "default" : "outline"}
                  className="w-full"
                  disabled={loadingTier !== null}
                >
                  {isCurrentLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processando...
                    </>
                  ) : isPaid ? (
                    "Começar teste grátis"
                  ) : (
                    "Começar grátis"
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-start pt-2">
        <Button variant="ghost" onClick={onBack} disabled={loadingTier !== null}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
      </div>
    </div>
  );
}
